import { XIcon } from 'lucide-react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, InputError, InputGroup, InputGroupInput } from '@crm/shared/ui';

import { ColorPicker } from './ColorPicker';
import { getColorPreviewBgColor } from './model/colorPickerStyles';
import { MAX_TITLE_LENGTH } from './model/consts';
import { useEditColumnForm } from './model/useEditColumnForm';

import type { TEditColumnFormState } from './model/editColumnForm.schema';
import type { TDealColumnColor } from '@crm/entities/deals-board';

export const EditColumn = ({
    columnColors,
    defaultColor,
    defaultTitle = '',
    disabled = false,
    skipSubmitWhenUnchanged = false,
    onClose,
    onColorPreviewChange,
    onSubmit,
}: {
    columnColors: TDealColumnColor[];
    defaultColor: string;
    defaultTitle?: string;
    disabled?: boolean;
    skipSubmitWhenUnchanged?: boolean;
    onClose: () => void;
    onColorPreviewChange?: (color: string, bgColor: string) => void;
    onSubmit: (data: TEditColumnFormState) => Promise<void>;
}) => {
    const { t } = useTranslation();
    const handleSubmitWithDefaultTitle = (data: TEditColumnFormState) =>
        onSubmit({
            ...data,
            title: data.title || t('crm.deals.board.column.defaultTitle'),
        });

    const {
        control,
        handleFormBlur,
        handleFormKeyDown,
        isColorPickerOpen,
        isDirty,
        setIsColorPickerOpen,
        submitForm,
    } = useEditColumnForm({
        defaultColor,
        defaultTitle,
        skipSubmitWhenUnchanged,
        onClose,
        onSubmit: handleSubmitWithDefaultTitle,
    });

    return (
        <form
            className="flex gap-4 w-full items-center"
            onBlur={handleFormBlur}
            onKeyDown={handleFormKeyDown}
            onSubmit={submitForm}
        >
            <Button
                aria-hidden="true"
                className="sr-only"
                disabled={disabled}
                tabIndex={-1}
                type="submit"
            />
            <div className="flex gap-1.5 items-center w-full">
                <Controller
                    control={control}
                    name="color"
                    render={({ field }) => {
                        const handleColorChange = (value: string) => {
                            field.onChange(value);
                            onColorPreviewChange?.(
                                value,
                                getColorPreviewBgColor(value, columnColors)
                            );
                        };

                        return (
                            <ColorPicker
                                columnColors={columnColors}
                                value={field.value}
                                open={isColorPickerOpen}
                                submitOnEnter={isDirty}
                                disabled={disabled}
                                onChange={handleColorChange}
                                onOpenChange={setIsColorPickerOpen}
                                onSubmit={() => {
                                    submitForm();
                                }}
                            />
                        );
                    }}
                />
                <Controller
                    control={control}
                    name="title"
                    render={({ field, fieldState }) => (
                        <InputGroup className="h-7 bg-white">
                            <InputGroupInput
                                {...field}
                                aria-invalid={fieldState.invalid}
                                maxLength={MAX_TITLE_LENGTH}
                                className="h-7 text-xs md:text-xs"
                                disabled={disabled}
                                ref={(element) => {
                                    field.ref(element);
                                }}
                                value={field.value ?? ''}
                                autoFocus
                                onBlur={field.onBlur}
                            />
                            {fieldState.invalid && (
                                <InputError
                                    errorMessage={fieldState.error?.message}
                                    iconClassName="size-3.5"
                                    tooltipSide="bottom"
                                />
                            )}
                        </InputGroup>
                    )}
                />
            </div>
            <Button
                aria-label={t('crm.deals.board.column.edit.closeAriaLabel')}
                className="size-7"
                size="icon-sm"
                type="button"
                variant="ghost"
                onClick={onClose}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.stopPropagation();
                    }
                }}
            >
                <XIcon />
            </Button>
        </form>
    );
};
